/**
 * ErrorBoundary — a screen that crashes shows a friendly card with "Try
 * again" instead of a blank white page (which, at a till, looks like Eaze
 * is down). Mounted around the whole app; reset when the route changes so
 * moving to another page clears the error.
 */
import React from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: React.ReactNode;
  /** Change this (e.g. the route) to clear a shown error. */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    // A new deploy can leave old page chunks missing — a reload fixes it.
    const chunk = /Loading chunk|dynamically imported module|Importing a module script failed/i.test(this.state.error.message);
    return (
      <div role="alert" className="flex min-h-[60vh] items-center justify-center p-6">
        <div className="max-w-md space-y-4 rounded-2xl border bg-card p-6 text-center shadow-sm">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/50">
            <AlertTriangle className="h-6 w-6 text-amber-700 dark:text-amber-300" aria-hidden />
          </span>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">{chunk ? "Eaze was updated" : "Something went wrong on this screen"}</h1>
            <p className="text-sm text-muted-foreground">
              {chunk
                ? "Reload to get the latest version. Nothing you saved is lost."
                : "Your data is safe. Try again, or go back to the previous page. If it keeps happening, tell us what you were doing."}
            </p>
          </div>
          <div className="flex justify-center gap-2">
            {!chunk && (
              <Button variant="outline" onClick={() => this.setState({ error: null })}>
                Try again
              </Button>
            )}
            <Button className="bg-[#4CAF50] text-white hover:bg-[#45A049]" onClick={() => window.location.reload()}>
              <RefreshCcw className="mr-1.5 h-4 w-4" aria-hidden /> Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
